import { useState, useEffect, useRef, useCallback } from 'react'
import throttle from 'lodash/throttle'

export interface FaceTrackingOptions {
  onFaceDetectionChange?: (detected: boolean) => void
  onMultipleFacesChange?: (multipleFaces: boolean, numFaces: number) => void
  onGazeChange?: (isLookingAway: boolean) => void
  onEvent?: (event: { type: string; timestamp: Date }) => void
}

export interface FaceTrackingState {
  faceDetected: boolean
  multipleFaces: boolean
  isLookingAway: boolean
  handleGazeUpdate: (lookingAway: boolean) => void
  updateFaceDetection: (detected: boolean, numFaces?: number) => void
}

export const useFaceTracking = (
  videoRef: React.RefObject<HTMLVideoElement>,
  isActive: boolean,
  options: FaceTrackingOptions
): FaceTrackingState => {
  const faceDetectedRef = useRef(false)
  const multipleFacesRef = useRef(false)
  const isLookingAwayRef = useRef(false)

  const [faceDetected, setFaceDetected] = useState(false)
  const [multipleFaces, setMultipleFaces] = useState(false)
  const [isLookingAway, setIsLookingAway] = useState(false)

  const awayStartTimeRef = useRef<Date | null>(null)
  const consecutiveNoFaceDetectionsRef = useRef(0)
  const consecutiveFaceDetectionsRef = useRef(0)

  useEffect(() => {
    faceDetectedRef.current = faceDetected
  }, [faceDetected])

  useEffect(() => {
    multipleFacesRef.current = multipleFaces
  }, [multipleFaces])

  useEffect(() => {
    isLookingAwayRef.current = isLookingAway
  }, [isLookingAway])

  const handleGazeUpdate = useCallback(
    throttle((lookingAway: boolean) => {
      if (lookingAway) {
        if (!isLookingAwayRef.current) {
          isLookingAwayRef.current = true
          setIsLookingAway(true)
          options.onGazeChange?.(true)
          awayStartTimeRef.current = new Date()
        }
      } else {
        if (isLookingAwayRef.current) {
          isLookingAwayRef.current = false
          setIsLookingAway(false)
          options.onGazeChange?.(false)

          if (awayStartTimeRef.current) {
            const awayDuration = new Date().getTime() - awayStartTimeRef.current.getTime()
            if (awayDuration > 2000) {
              options.onEvent?.({
                type: 'LOOKING_AWAY_ALERT',
                timestamp: new Date(),
              })
            }
            awayStartTimeRef.current = null
          }
        }
      }
    }, 50),
    [options]
  )

  const updateFaceDetection = useCallback(
    (detected: boolean, numFaces?: number) => {
      if (!isActive) return

      const faceCount = detected ? numFaces : 0

      if (!detected) {
        options.onMultipleFacesChange?.(false, 0)
        consecutiveNoFaceDetectionsRef.current++
        consecutiveFaceDetectionsRef.current = 0

        if (consecutiveNoFaceDetectionsRef.current >= 2) {
          if (faceDetectedRef.current) {
            faceDetectedRef.current = false
            setFaceDetected(false)
            options.onMultipleFacesChange?.(false, 0)
            options.onEvent?.({
              type: 'NO_FACE_DETECTED',
              timestamp: new Date(),
            })
          }
        }
      } else {
        consecutiveNoFaceDetectionsRef.current = 0
        consecutiveFaceDetectionsRef.current++

        if (faceCount > 1) {
          if (!multipleFacesRef.current) {
            multipleFacesRef.current = true
            setMultipleFaces(true)
            options.onEvent?.({
              type: 'MULTIPLE_FACES_DETECTED',
              timestamp: new Date(),
            })
          }
          options.onMultipleFacesChange?.(true, faceCount)
        } else if (multipleFacesRef.current) {
          multipleFacesRef.current = false
          setMultipleFaces(false)
          options.onMultipleFacesChange?.(false, faceCount)
        } else {
          options.onMultipleFacesChange?.(false, faceCount)
        }

        if (consecutiveFaceDetectionsRef.current >= 1) {
          if (!faceDetectedRef.current) {
            faceDetectedRef.current = true
            setFaceDetected(true)
            options.onFaceDetectionChange?.(true)
            options.onEvent?.({
              type: 'FACE_DETECTED',
              timestamp: new Date(),
            })
          }
        }
      }
    },
    [options, isActive]
  )

  return {
    faceDetected,
    multipleFaces,
    isLookingAway,
    handleGazeUpdate,
    updateFaceDetection,
  }
}
